import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Maximize2, Minimize2, AlertCircle, Loader } from 'lucide-react';
import { Button } from '../../../ui/Button';

interface ChartContainerProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  controls?: React.ReactNode;
  loading?: boolean;
  error?: string;
  maximizable?: boolean;
  maximized?: boolean;
  onMaximize?: () => void;
  onMinimize?: () => void;
  className?: string;
  /** Grow with content instead of filling a fixed-height parent; avoids inner scroll (page scrolls). Ignored when maximized. */
  intrinsicHeight?: boolean;
}

const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  subtitle,
  children,
  controls,
  loading = false,
  error,
  maximizable = false,
  maximized = false,
  onMaximize,
  onMinimize,
  className = '',
  intrinsicHeight = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMaximized, setIsMobileMaximized] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track container width for responsive sizing
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate responsive font sizes based on container width
  // More aggressive scaling to prevent text stacking
  const titleFontSize = useMemo(() => {
    if (containerWidth < 250) return '0.5rem'; // 8px
    if (containerWidth < 350) return '0.625rem'; // 10px
    if (containerWidth < 450) return '0.75rem'; // 12px
    if (containerWidth < 550) return '0.875rem'; // 14px
    if (containerWidth < 700) return '1rem'; // 16px
    if (containerWidth < 900) return '1.125rem'; // 18px
    return '1.25rem'; // 20px
  }, [containerWidth]);

  const subtitleFontSize = useMemo(() => {
    if (containerWidth < 250) return '0.4375rem'; // 7px
    if (containerWidth < 350) return '0.5625rem'; // 9px
    if (containerWidth < 450) return '0.625rem'; // 10px
    if (containerWidth < 550) return '0.75rem'; // 12px
    if (containerWidth < 700) return '0.875rem'; // 14px
    return '0.875rem'; // 14px
  }, [containerWidth]);

  // Handle mobile maximize
  const handleMobileMaximize = () => {
    setIsMobileMaximized(true);
  };

  const handleMobileMinimize = () => {
    setIsMobileMaximized(false);
  };

  // Mobile fullscreen mode
  if (isMobile && isMobileMaximized) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Mobile maximize header - minimal controls only */}
        <div className="flex justify-between items-center p-3 border-b border-gray-200 bg-gray-50">
          <span className="text-sm font-medium text-gray-700">Chart View</span>
          <div className="flex items-center gap-2">
            {controls && <div className="flex items-center gap-2">{controls}</div>}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleMobileMinimize}
              aria-label="Minimize chart"
              className="flex items-center gap-1"
            >
              <Minimize2 size={16} />
              <span>Minimize</span>
            </Button>
          </div>
        </div>

        {/* Full-screen chart content */}
        <div className="flex-1 overflow-auto p-2 custom-scrollbar">{children}</div>
      </div>
    );
  }

  const containerClasses = maximized
    ? 'fixed inset-4 z-50 bg-white rounded-lg shadow-2xl flex flex-col max-h-[calc(100vh-2rem)]'
    : intrinsicHeight
      ? `bg-white rounded-lg border border-rowing-gray-200 flex flex-col h-auto min-h-0 ${className}`
      : `bg-white rounded-lg border border-rowing-gray-200 flex flex-col h-full ${className}`;

  const renderContent = () => {
    if (loading) {
      return (
        <div
          className={`flex flex-col items-center justify-center animate-pulse ${
            intrinsicHeight ? '' : 'flex-1'
          }`}
          style={{ minHeight: 'clamp(300px, 50vh, 600px)' }}
          data-testid="chart-loading"
        >
          <Loader className="animate-spin mb-4 text-rowing-blue" size={32} />
          <p className="text-rowing-gray-500">Loading chart data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div
          className={`flex flex-col items-center justify-center px-4 py-10 ${
            intrinsicHeight ? '' : 'flex-1'
          }`}
          data-testid="chart-error"
        >
          <AlertCircle className="mb-4 text-rowing-red" size={32} />
          <h4 className="text-lg font-semibold text-rowing-navy mb-2">Unable to load chart</h4>
          <p className="text-rowing-red text-center">{error}</p>
        </div>
      );
    }

    const contentScrollClasses =
      maximized || !intrinsicHeight
        ? 'flex-1 overflow-auto custom-scrollbar min-h-0'
        : 'overflow-visible';

    return (
      <div className={`sm:p-2 flex flex-col ${contentScrollClasses}`} data-testid="chart-content">
        {children}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={containerClasses} data-testid="chart-container">
      {/* Chart Header */}
      <div
        className="sm:p-3 border-b border-rowing-gray-200 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 sm:gap-4"
        data-testid="chart-header"
      >
        <div className="flex-1 min-w-0">
          <h3
            className="font-semibold text-rowing-navy mb-1 leading-tight"
            style={{ fontSize: titleFontSize }}
          >
            {title}
          </h3>
          {subtitle && (
            <p
              className="text-rowing-gray-600 leading-tight"
              style={{ fontSize: subtitleFontSize }}
            >
              {subtitle}
            </p>
          )}
        </div>

        {/* Controls and Actions */}
        <div className="flex items-center gap-2 flex-wrap justify-left xl:justify-left w-full xl:w-auto">
          {controls && (
            <div className="flex items-center gap-2 flex-wrap mr-0 xl:mr-2 max-w-full w-full xl:w-auto">
              {controls}
            </div>
          )}

          {/* Maximize/Minimize Button */}
          {maximizable && !maximized && onMaximize && (
            <Button
              variant="secondary"
              size="sm"
              onClick={isMobile ? handleMobileMaximize : onMaximize}
              aria-label="Maximize chart"
              className="flex items-center gap-1"
            >
              <Maximize2 size={16} />
              <span className="hidden sm:inline">Maximize</span>
            </Button>
          )}

          {maximized && onMinimize && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onMinimize}
              aria-label="Minimize chart"
              className="flex items-center gap-1"
            >
              <Minimize2 size={16} />
              <span className="hidden sm:inline">Minimize</span>
            </Button>
          )}
        </div>
      </div>

      {/* Chart Content */}
      {renderContent()}
    </div>
  );
};

export default ChartContainer;
